/**
 * Custom Embed Drag-and-Drop Extension
 *
 * Komplett eigenes Drag-and-Drop-System für Embed-Cards (Audit, Doc, Scene, Annotation).
 * Umgeht TipTap/ProseMirror's eingebauten (und für React-NodeViews unzuverlässigen)
 * Drag-Mechanismus. Nutzt stattdessen native HTML5 Drag Events + ProseMirror-Transaktionen.
 *
 * Ablauf:
 *   1. Grip-Element hat draggable="true" + onDragStart → speichert Node-Position in dataTransfer
 *   2. Dieses Plugin fängt dragover/drop/dragleave auf Editor-Ebene ab
 *   3. dragover: zeigt Drop-Indikator-Linie, preventDefault für Drop-Erlaubnis
 *   4. drop: liest Position, verschiebt Node per ProseMirror-Transaktion
 */

import { Extension } from '@tiptap/core'
import { Plugin, PluginKey } from '@tiptap/pm/state'

export const EMBED_DRAG_DATA = 'application/x-embed-drag'

export const EmbedDragDrop = Extension.create({
  name: 'embedDragDrop',

  addProseMirrorPlugins() {
    const editor = this.editor

    return [
      new Plugin({
        key: new PluginKey('embedDragDrop'),
        props: {
          handleDOMEvents: {
            dragover(view, event) {
              // Nur reagieren, wenn ein Embed gezogen wird
              if (!event.dataTransfer?.types.includes(EMBED_DRAG_DATA)) return false

              event.preventDefault()
              event.dataTransfer.dropEffect = 'move'

              // Drop-Indikator positionieren
              const editorDom = view.dom as HTMLElement
              let indicator = editorDom.querySelector('.embed-drop-indicator') as HTMLElement
              if (!indicator) {
                indicator = document.createElement('div')
                indicator.className = 'embed-drop-indicator'
                editorDom.style.position = 'relative'
                editorDom.appendChild(indicator)
              }

              // Finde den nächsten Block-Rand für die Indikator-Linie
              const coords = { left: event.clientX, top: event.clientY }
              const pos = view.posAtCoords(coords)
              if (!pos) {
                indicator.style.display = 'none'
                return true
              }

              // Resolve to nearest block boundary
              const doc = view.state.doc
              const $pos = doc.resolve(pos.pos)

              let insertPos: number
              if ($pos.parent === doc) {
                // Between top-level blocks
                insertPos = pos.pos
              } else {
                // Inside a block — decide before or after
                const blockStart = $pos.before($pos.depth)
                const blockEnd = $pos.after($pos.depth)
                const startCoords = view.coordsAtPos(blockStart)
                const endCoords = view.coordsAtPos(blockEnd)
                const midY = (startCoords.top + endCoords.bottom) / 2
                insertPos = event.clientY < midY ? blockStart : blockEnd
              }

              // Position indicator line
              try {
                const indicatorCoords = view.coordsAtPos(insertPos)
                const editorRect = editorDom.getBoundingClientRect()
                const y = indicatorCoords.top - editorRect.top

                indicator.style.display = 'block'
                indicator.style.top = `${y}px`
                indicator.dataset.insertPos = String(insertPos)
              } catch {
                indicator.style.display = 'none'
              }

              return true
            },

            dragleave(view, event) {
              // Nur ausblenden, wenn wirklich den Editor verlassen
              const relatedTarget = event.relatedTarget as HTMLElement | null
              if (relatedTarget && view.dom.contains(relatedTarget)) return false

              const indicator = view.dom.querySelector('.embed-drop-indicator') as HTMLElement
              if (indicator) indicator.style.display = 'none'
              return false
            },

            drop(view, event) {
              if (!event.dataTransfer?.types.includes(EMBED_DRAG_DATA)) return false

              event.preventDefault()

              // Indikator entfernen
              const indicator = view.dom.querySelector('.embed-drop-indicator') as HTMLElement
              const insertPosStr = indicator?.dataset.insertPos
              if (indicator) indicator.style.display = 'none'

              // Daten aus dataTransfer lesen
              let data: { pos: number }
              try {
                data = JSON.parse(event.dataTransfer.getData(EMBED_DRAG_DATA))
              } catch {
                return true
              }

              const fromPos = data.pos
              const doc = view.state.doc
              const node = doc.nodeAt(fromPos)
              if (!node) return true

              // Zielposition bestimmen (aus Indikator oder neu berechnen)
              let targetPos: number
              if (insertPosStr != null) {
                targetPos = Number(insertPosStr)
              } else {
                const coords = { left: event.clientX, top: event.clientY }
                const posResult = view.posAtCoords(coords)
                if (!posResult) return true
                targetPos = posResult.pos
              }

              // Wenn gleiche Position — nichts tun
              const nodeSize = node.nodeSize
              if (targetPos >= fromPos && targetPos <= fromPos + nodeSize) {
                return true
              }

              // ProseMirror-Transaktion: Node löschen und an neuer Position einfügen
              const { tr } = view.state

              // Wenn Ziel nach der Quelle liegt, muss nach dem Löschen korrigiert werden
              if (targetPos > fromPos) {
                tr.delete(fromPos, fromPos + nodeSize)
                tr.insert(targetPos - nodeSize, node)
              } else {
                tr.delete(fromPos, fromPos + nodeSize)
                tr.insert(targetPos, node)
              }

              view.dispatch(tr)
              return true
            },

            dragend(view) {
              const indicator = view.dom.querySelector('.embed-drop-indicator') as HTMLElement
              if (indicator) indicator.style.display = 'none'
              return false
            },
          },
        },
      }),
    ]
  },
})
